import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const TrendIndicator = ({ prev, current }) => {
   if (prev === undefined || prev === null) return <Minus size={16} className="text-muted opacity-25" />;
   
   const diff = prev - current;
   
   if (diff > 0) return (
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="d-flex align-items-center text-success small fw-bold">
         <TrendingUp size={16} className="me-1" /> {diff}
      </motion.div>
   );
   if (diff < 0) return (
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="d-flex align-items-center text-danger small fw-bold">
         <TrendingDown size={16} className="me-1" /> {Math.abs(diff)}
      </motion.div>
   );
   return <Minus size={16} className="text-secondary opacity-50" />;
};

const FullScreenLeaderboard = ({ players }) => {
  const [currentPage, setCurrentPage] = React.useState(0);
  const itemsPerPage = 12;

  // Sort players by score (desc)
  const sortedPlayers = [...players].sort((a, b) => b.score - a.score);
  const podiumPlayers = sortedPlayers.slice(0, 3);
  const remainingPlayers = sortedPlayers.slice(3);

  const totalPages = Math.ceil(remainingPlayers.length / itemsPerPage);
  const currentItems = remainingPlayers.slice(
     currentPage * itemsPerPage,
     (currentPage + 1) * itemsPerPage
  );

  return (
    <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-light position-relative overflow-hidden">
       {/* Background decoration */}
       <div className="position-absolute top-0 start-0 w-100 h-100 opacity-25" style={{zIndex: 0}}>
          <div className="position-absolute top-50 start-50 translate-middle bg-primary rounded-circle blur-3xl opacity-20" style={{width: '50vw', height: '50vw', maxWidth: '800px', maxHeight: '800px', filter: 'blur(100px)'}}></div>
       </div>

       <div className="position-relative z-1 container text-center h-100 d-flex flex-column py-4">
          <motion.div
             initial={{ opacity: 0, y: -20 }}
             animate={{ opacity: 1, y: 0 }}
             className="mb-3 flex-shrink-0"
          >
             <h1 className="display-5 fw-bold text-dark mb-1">Liderlik Tablosu</h1>
             <p className="lead text-secondary small mb-0">İşte şu anki durum!</p>
          </motion.div>

          {/* Podium for Top 3 */}
          <div className="d-flex flex-wrap align-items-end justify-content-center gap-3 gap-md-4 mb-4 flex-shrink-0" style={{minHeight: 220}}>
             {/* 2nd Place */}
             {podiumPlayers[1] && (
                <motion.div 
                   initial={{ height: 0, opacity: 0 }}
                   animate={{ height: 'auto', opacity: 1 }}
                   transition={{ delay: 0.5, duration: 0.5 }}
                   className="d-flex flex-column align-items-center text-center order-1 order-md-0"
                   style={{width: '130px', maxWidth: '30%'}}
                >
                   <div className="mb-2 fw-bold text-truncate w-100 small">{podiumPlayers[1].nickname}</div>
                   <div className="bg-secondary bg-gradient text-white d-flex flex-column align-items-center justify-content-center rounded-top shadow" style={{width: '100%', height: 110}}>
                      <div className="h2 fw-bold mb-0">2</div>
                      <div className="bg-white bg-opacity-25 px-2 rounded-pill mt-1">
                         <TrendIndicator prev={podiumPlayers[1].prevRank} current={1} />
                      </div>
                   </div>
                   <div className="mt-1 fw-bold text-secondary small">{podiumPlayers[1].score} P</div>
                </motion.div>
             )}

             {/* 1st Place */}
             {podiumPlayers[0] && (
                <motion.div 
                   initial={{ height: 0, opacity: 0 }}
                   animate={{ height: 'auto', opacity: 1 }}
                   transition={{ delay: 0.2, duration: 0.5 }}
                   className="d-flex flex-column align-items-center text-center order-0 order-md-1"
                   style={{width: '160px', maxWidth: '35%'}}
                >
                   <Trophy size={40} className="text-warning mb-2 drop-shadow" fill="currentColor" />
                   <div className="mb-1 fw-bold text-truncate w-100">{podiumPlayers[0].nickname}</div>
                   <div className="bg-warning bg-gradient text-white d-flex flex-column align-items-center justify-content-center rounded-top shadow-lg" style={{width: '100%', height: 150}}>
                      <div className="display-6 fw-bold mb-0">1</div>
                      <div className="bg-white bg-opacity-25 px-2 rounded-pill mt-1">
                         <TrendIndicator prev={podiumPlayers[0].prevRank} current={0} />
                      </div>
                   </div>
                   <div className="mt-2 h5 fw-bold text-warning">{podiumPlayers[0].score} P</div>
                </motion.div>
             )}

             {/* 3rd Place */}
             {podiumPlayers[2] && (
                <motion.div 
                   initial={{ height: 0, opacity: 0 }}
                   animate={{ height: 'auto', opacity: 1 }}
                   transition={{ delay: 0.8, duration: 0.5 }}
                   className="d-flex flex-column align-items-center text-center order-2"
                   style={{width: '130px', maxWidth: '30%'}}
                >
                   <div className="mb-2 fw-bold text-truncate w-100 small">{podiumPlayers[2].nickname}</div>
                   <div className="bg-danger bg-gradient text-white d-flex flex-column align-items-center justify-content-center rounded-top shadow" style={{width: '100%', height: 90}}>
                      <div className="h2 fw-bold mb-0">3</div>
                      <div className="bg-white bg-opacity-25 px-2 rounded-pill mt-1">
                         <TrendIndicator prev={podiumPlayers[2].prevRank} current={2} />
                      </div>
                   </div>
                   <div className="mt-1 fw-bold text-danger small">{podiumPlayers[2].score} P</div>
                </motion.div>
             )}
          </div>

          {/* Paginated List for remaining players */}
          <div className="flex-fill overflow-hidden px-5 d-flex flex-column">
             <div className="row g-2 justify-content-center flex-fill overflow-auto py-2">
                {currentItems.map((player, idx) => {
                   const globalIdx = 3 + (currentPage * itemsPerPage) + idx;
                   return (
                      <div className="col-12 col-md-6 col-lg-4" key={player.nickname}>
                         <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="bg-white rounded-3 shadow-sm p-3 d-flex align-items-center gap-3 border h-100"
                         >
                            <div className="badge bg-light text-dark rounded-circle p-0 d-flex align-items-center justify-content-center fs-6 border flex-shrink-0" style={{width: 32, height: 32}}>
                               {globalIdx + 1}
                            </div>
                            <div className="fw-bold flex-fill text-start text-truncate small">{player.nickname}</div>
                            
                            <div className="me-2">
                               <TrendIndicator prev={player.prevRank} current={globalIdx} />
                            </div>

                            <div className="fw-bold font-monospace text-primary small">{player.score} P</div>
                         </motion.div>
                      </div>
                   );
                })}
             </div>

             {/* Pagination Controls */}
             {totalPages > 1 && (
                <div className="mt-3 mb-2 d-flex align-items-center justify-content-center gap-3 flex-shrink-0">
                   <button 
                      className="btn btn-sm btn-outline-primary rounded-pill px-3 fw-bold"
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={currentPage === 0}
                   >
                      &larr; Önceki
                   </button>
                   <span className="text-secondary small fw-bold">
                      Sayfa {currentPage + 1} / {totalPages}
                   </span>
                   <button 
                      className="btn btn-sm btn-outline-primary rounded-pill px-3 fw-bold"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                      disabled={currentPage === totalPages - 1}
                   >
                      Sonraki &rarr;
                   </button>
                </div>
             )}

             {remainingPlayers.length === 0 && (
                <div className="text-muted mt-4 small">Başka oyuncu yok.</div>
             )}
          </div>
       </div>
    </div>
  );
};

export default FullScreenLeaderboard;
